import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import StaticPageLayout from '../../components/StaticPageLayout';
import { useThemeStore } from '../../store/theme';

const generalFAQLinks = [
  { title: 'How to reach and communicate with SweetPilot\'s customer support', href: '#customer-support' },
  { title: 'How do I find my chats with AI employees that appear to be gone?', href: '#find-chats' },
  { title: 'Unable to select social media pages for weekly posting automation', href: '#social-media-automation' },
  { title: 'AI employee can\'t process the link to my website in the onboarding process', href: '#onboarding-link' },
  { title: 'Email entered at checkout has a typo/mistyped, wrong email / need to use a different email', href: '#checkout-email' },
  { title: 'I\'m unable to post to Facebook and/or Instagram', href: '#facebook-instagram-post' },
  { title: 'How to change the timezone AI employees work in?', href: '#change-timezone' },
  { title: 'Reconnecting an integration in SweetPilot from scratch', href: '#reconnect-integration' },
];

const errorMessagesLinks = [
  { title: 'Authentication error on SweetPilot browser app', href: '#auth-error' },
  { title: 'Error: Loading chunk failed', href: '#loading-chunk-error' },
  { title: 'Error: [Function call] received an unexpected keyword argument [when trying to take actions on other platforms]', href: '#function-call-error' },
  { title: '"There was an issue" error appeared when adding a connection in settings', href: '#connection-issue-error' },
  { title: 'An error occurred while using the tool: \'id\' / Image upload failed', href: '#image-upload-error' },
  { title: '"You need to be subscribed to access this resource" error', href: '#subscription-error' },
  { title: '"Access denied" error when accessing generated images', href: '#access-denied-error' },
  { title: 'Network error on browser app', href: '#network-error' },
  { title: '"AxiosError: Request failed 400" at checkout', href: '#checkout-error' },
];

const loginIssuesLinks = [
  { title: 'Didn\'t receive the one-time login code', href: '#login-code' },
  { title: 'Login screen is blank, white or doesn\'t work (browser app)', href: '#blank-login' },
  { title: 'Unable to press "continue with email" in login screen (browser)', href: '#continue-email' },
  { title: 'App is blank after login (mobile)', href: '#mobile-blank' },
  { title: 'Can\'t log in with Google or GitHub', href: '#social-login' },
  { title: 'Session expired unexpectedly', href: '#session-expired' },
];

const aiEmployeesIssuesLinks = [
  { title: 'AI employees don\'t help', href: '#employees-dont-help' },
  { title: 'AI employee is creating a document, but the canvas keeps loading', href: '#canvas-loading' },
  { title: 'AI employee stops responding', href: '#stops-responding' },
  { title: 'My Instagram and Facebook page are connected but won\'t cross post', href: '#cross-post' },
  { title: 'AI employee says it will do something later', href: '#do-later' },
  { title: 'AI employee response is too slow', href: '#slow-response' },
  { title: 'AI employee gives incorrect or outdated information', href: '#incorrect-info' },
];

const troubleshootingStepsLinks = [
  { title: 'Opening a new chat with an AI employee: when and why', href: '#new-chat' },
  { title: 'Clearing browser\'s cache and cookies: when and why', href: '#clear-cache' },
  { title: 'Checking your internet connection', href: '#check-internet' },
  { title: 'Trying a different browser', href: '#different-browser' },
  { title: 'Disabling browser extensions', href: '#disable-extensions' },
];

const feedbackLinks = [
  { title: 'How to submit feature requests to SweetPilot?', href: '#feature-requests' },
  { title: 'How to submit feedback for SweetPilot?', href: '#submit-feedback' },
  { title: 'Where can I see the product roadmap?', href: '/roadmap' },
  { title: 'How to report a bug', href: '#report-bug' },
];

const billingIssuesLinks = [
  { title: 'Payment failed at checkout', href: '#payment-failed' },
  { title: 'Charged twice for subscription', href: '#charged-twice' },
  { title: 'Credits not added after purchase', href: '#credits-not-added' },
  { title: 'Unable to cancel subscription', href: '#cancel-subscription' },
  { title: 'Invoice not received', href: '#invoice-missing' },
];

const integrationIssuesLinks = [
  { title: 'Google integration not working', href: '#google-integration' },
  { title: 'Slack messages not sending', href: '#slack-messages' },
  { title: 'Trello boards not syncing', href: '#trello-sync' },
  { title: 'Calendar events not appearing', href: '#calendar-events' },
  { title: 'Email integration disconnected', href: '#email-disconnected' },
];

interface LinkItemProps {
  title: string;
  href: string;
  isDark: boolean;
}

function LinkItem({ title, href, isDark }: LinkItemProps) {
  return (
    <Link
      to={href}
      className={`flex items-center justify-between py-4 px-2 transition-all group border-b ${
        isDark
          ? 'border-white/5 hover:bg-white/5'
          : 'border-slate-100 hover:bg-slate-50'
      }`}
    >
      <span className={`font-medium transition-colors ${
        isDark ? 'text-white group-hover:text-purple-300' : 'text-slate-900 group-hover:text-purple-600'
      }`}>
        {title}
      </span>
      <ChevronRight className={`w-5 h-5 transition-colors ${
        isDark ? 'text-gray-600 group-hover:text-purple-400' : 'text-slate-300 group-hover:text-purple-600'
      }`} />
    </Link>
  );
}

interface SectionCardProps {
  title: string;
  links: Array<{ title: string; href: string }>;
  isDark: boolean;
  delay?: number;
}

function SectionCard({ title, links, isDark, delay = 0 }: SectionCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className={`rounded-2xl border overflow-hidden ${
        isDark
          ? 'bg-dark-800/50 border-white/5'
          : 'bg-white border-slate-200 shadow-sm'
      }`}
    >
      <div className={`px-6 py-5 border-b ${isDark ? 'border-white/5' : 'border-slate-100'}`}>
        <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{title}</h2>
      </div>
      <div className="px-4">
        {links.map((link) => (
          <LinkItem
            key={link.title + link.href}
            title={link.title}
            href={link.href}
            isDark={isDark}
          />
        ))}
      </div>
    </motion.div>
  );
}

export default function TroubleshootingFAQDocs() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  return (
    <StaticPageLayout
      title=""
      subtitle=""
      showBackButton={false}
    >
      {/* Breadcrumbs */}
      <nav className="mb-8">
        <ol className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
          <li>
            <Link
              to="/docs"
              className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}
            >
              All Collections
            </Link>
          </li>
          <li>
            <ChevronRight className="w-4 h-4" />
          </li>
          <li className={isDark ? 'text-white' : 'text-slate-900'}>
            Troubleshooting & FAQ
          </li>
        </ol>
      </nav>

      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-10"
      >
        <h1 className={`text-4xl font-bold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
          Troubleshooting & FAQ
        </h1>
        <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
          Find answers to common questions and issues.
        </p>
      </motion.div>

      {/* General FAQ */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className={`rounded-2xl border overflow-hidden mb-6 ${
          isDark
            ? 'bg-dark-800/50 border-white/5'
            : 'bg-white border-slate-200 shadow-sm'
        }`}
      >
        <div className="px-4">
          {generalFAQLinks.map((link) => (
            <LinkItem
              key={link.title}
              title={link.title}
              href={link.href}
              isDark={isDark}
            />
          ))}
        </div>
      </motion.div>

      {/* Troubleshooting error messages */}
      <SectionCard
        title="Troubleshooting error messages"
        links={errorMessagesLinks}
        isDark={isDark}
        delay={0.2}
      />

      <div className="h-6" />

      {/* Troubleshooting login issues */}
      <SectionCard
        title="Troubleshooting login issues"
        links={loginIssuesLinks}
        isDark={isDark}
        delay={0.3}
      />

      <div className="h-6" />

      {/* Troubleshooting AI Employees */}
      <SectionCard
        title="Troubleshooting AI Employees"
        links={aiEmployeesIssuesLinks}
        isDark={isDark}
        delay={0.4}
      />

      <div className="h-6" />

      {/* Troubleshooting billing issues */}
      <SectionCard
        title="Troubleshooting billing issues"
        links={billingIssuesLinks}
        isDark={isDark}
        delay={0.5}
      />

      <div className="h-6" />

      {/* Troubleshooting integration issues */}
      <SectionCard
        title="Troubleshooting integration issues"
        links={integrationIssuesLinks}
        isDark={isDark}
        delay={0.6}
      />

      <div className="h-6" />

      {/* Why do we offer certain troubleshooting steps? */}
      <SectionCard
        title="Why do we offer certain troubleshooting steps?"
        links={troubleshootingStepsLinks}
        isDark={isDark}
        delay={0.7}
      />

      <div className="h-6" />

      {/* Feedback & feature requests */}
      <SectionCard
        title="Feedback & feature requests"
        links={feedbackLinks}
        isDark={isDark}
        delay={0.8}
      />

      <div className="h-16" />

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.9 }}
        className="text-center"
      >
        <div className={`w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center ${
          isDark ? 'bg-purple-500/20' : 'bg-purple-100'
        }`}>
          <span className="text-2xl">❓</span>
        </div>
        <a
          href="mailto:help@sweetpilot.com"
          className={`text-sm transition-colors ${
            isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'
          }`}
        >
          help@sweetpilot.com
        </a>
      </motion.div>
    </StaticPageLayout>
  );
}
